/**
 * googleDrive — Sprint 4C · Phase 2
 * Optional, deferred connection that uploads backups to a VISIBLE "Wayfare Backups"
 * folder in the user's own Google Drive (scope drive.file → the app only ever sees
 * files it created). The app holds NO client secret: the cPanel backend
 * (api/drive/*) does the OAuth exchange and keeps the refresh token; the client
 * only gets a short-lived access token. Backup bytes go client → Drive directly,
 * never through our server.
 *
 * Keeps the last 5 backups (rolling) and can list + download them for restore.
 */
import authManager from './authManager';

const API = './api/drive/';
const CONNECTED_KEY = 'wf_drive_connected';
const GAPI = 'https://www.googleapis.com/drive/v3';
const FOLDER_NAME = 'Wayfare Backups';

const post = async (endpoint) => {
  const resp = await fetch(API + endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: authManager.getToken() }),
  });
  return resp.json().catch(() => ({}));
};

export function isConnected() {
  return localStorage.getItem(CONNECTED_KEY) === 'true';
}

export async function checkConnected() {
  try {
    const data = await post('status.php');
    const connected = !!data.connected;
    localStorage.setItem(CONNECTED_KEY, connected ? 'true' : 'false');
    return connected;
  } catch {
    return isConnected();
  }
}

export async function connect() {
  const data = await post('auth-url.php');
  if (data.success && data.url) { window.location.href = data.url; return true; }
  throw new Error(data.error || 'auth_url_failed');
}

export async function disconnect() {
  await post('disconnect.php').catch(() => {});
  localStorage.setItem(CONNECTED_KEY, 'false');
}

async function getAccessToken() {
  const data = await post('access-token.php');
  if (data.success && data.access_token) {
    localStorage.setItem(CONNECTED_KEY, 'true');
    return data.access_token;
  }
  if (data.connected === false) localStorage.setItem(CONNECTED_KEY, 'false');
  return null;
}

// ── Drive REST helpers ───────────────────────────────────────────────────────
async function driveGet(token, path) {
  const r = await fetch(`${GAPI}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(`drive_${r.status}`);
  return r.json();
}

/** Find (or create) the visible "Wayfare Backups" folder. With drive.file the
 *  search only returns the folder the app itself created. */
async function ensureFolder(token) {
  const q = encodeURIComponent(`mimeType='application/vnd.google-apps.folder' and name='${FOLDER_NAME}' and trashed=false`);
  const res = await driveGet(token, `/files?q=${q}&fields=files(id,name)&spaces=drive`);
  if (res.files && res.files.length) return res.files[0].id;
  const r = await fetch(`${GAPI}/files?fields=id`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: FOLDER_NAME, mimeType: 'application/vnd.google-apps.folder' }),
  });
  if (!r.ok) throw new Error(`drive_folder_${r.status}`);
  return (await r.json()).id;
}

/** Find a file by exact name in the folder → its id, or null. */
async function findFileByName(token, folderId, name) {
  const q = encodeURIComponent(`'${folderId}' in parents and name='${name}' and trashed=false`);
  const res = await driveGet(token, `/files?q=${q}&fields=files(id)&spaces=drive`);
  return res.files && res.files[0] ? res.files[0].id : null;
}

/** Upsert a backup object as JSON into the Wayfare Backups folder BY NAME: if a
 *  file with that name already exists it is overwritten (one file per name —
 *  e.g. wayfare-backup.json full, wayfare-text.json light). No accumulation. */
export async function uploadBackup(backup, filename) {
  const token = await getAccessToken();
  if (!token) throw new Error('not_connected');
  const folderId = await ensureFolder(token);
  const existingId = await findFileByName(token, folderId, filename);

  const metadata = existingId ? { name: filename } : { name: filename, parents: [folderId] };
  const boundary = 'wf' + Math.random().toString(36).slice(2);
  const body = new Blob([
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
    JSON.stringify(metadata),
    `\r\n--${boundary}\r\nContent-Type: application/json\r\n\r\n`,
    JSON.stringify(backup),
    `\r\n--${boundary}--`,
  ], { type: `multipart/related; boundary=${boundary}` });

  const url = existingId
    ? `https://www.googleapis.com/upload/drive/v3/files/${existingId}?uploadType=multipart&fields=id`
    : `https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id`;
  const resp = await fetch(url, {
    method: existingId ? 'PATCH' : 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': `multipart/related; boundary=${boundary}` },
    body,
  });
  if (!resp.ok) {
    const err = await resp.text().catch(() => '');
    throw new Error(`drive_upload_failed_${resp.status}: ${err.slice(0, 200)}`);
  }
  return resp.json().catch(() => ({}));
}

/** List the backups in the folder, newest first: [{ id, name, modifiedTime, size }]. */
export async function listBackups() {
  const token = await getAccessToken();
  if (!token) return [];
  const folderId = await ensureFolder(token);
  const q = encodeURIComponent(`'${folderId}' in parents and trashed=false`);
  const res = await driveGet(token, `/files?q=${q}&fields=files(id,name,modifiedTime,size)&orderBy=modifiedTime desc&spaces=drive`);
  return res.files || [];
}

/** Download a backup's raw JSON text (for restore). */
export async function downloadBackupContent(fileId) {
  const token = await getAccessToken();
  if (!token) throw new Error('not_connected');
  const r = await fetch(`${GAPI}/files/${encodeURIComponent(fileId)}?alt=media`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(`drive_download_${r.status}`);
  return r.text();
}

/** Consume a ?drive=… return param after the consent redirect. */
export function consumeReturnStatus() {
  try {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('drive');
    if (!status) return null;
    if (status === 'connected') localStorage.setItem(CONNECTED_KEY, 'true');
    params.delete('drive');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    return status;
  } catch {
    return null;
  }
}
